// @ts-nocheck
import { Box, Skeleton, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import {
  FORTA_BLUE,
  FORTA_DOCS,
  FORTA_GRAY2,
  FORTA_SUCCESS,
  FORTA_WHITE,
} from '../../../../utils/colors';
import { formatSecondsToHHMM, formatSecondsToHHMM2 } from '../../../../helpers';
import ErrorAlertIcon from '../../../svgs/ErrorAlertIcon';

import type { IOption } from '../../../../utils/types';

interface WeeklyUtilization {
  name: string;
  parentSN: number;
  clientSN: number;
  session: number;
  clientSNExpectedHours: number;
}

const CustomTooltip = (t: TFunction<'dashboard'>, { payload = [], label }: any) => {
  const expectedClientSN = payload.find((data: any) => data.dataKey === 'clientSNExpectedHours');
  return (
    <Box
      sx={{
        backgroundColor: FORTA_WHITE,
        border: `1px solid ${FORTA_BLUE.primary}`,
        boxShadow: `0 4px 4px rgba(0, 0, 0, 0.2)`,
        p: 2,
        borderRadius: '10px',
        textAlign: 'left',
      }}
    >
      <Typography sx={{ mb: 2 }} fontSize={16}>{`${t(
        'utilization.sixWeekUtilization.weekStartingWith',
      )} ${label}`}</Typography>

      {payload.map((data: any) => {
        if (data.dataKey === 'clientSN') {
          return (
            <Typography
              key={data.dataKey}
              color={FORTA_DOCS.CSN_55}
              fontWeight="bold"
              fontSize={14}
            >{`Client supervision: ${formatSecondsToHHMM(data.value)} ${
              expectedClientSN ? ` / ${formatSecondsToHHMM(expectedClientSN.value)}` : ''
            }`}</Typography>
          );
        }
        if (data.dataKey === 'parentSN') {
          return (
            <Typography
              key={data.dataKey}
              color={FORTA_DOCS.PSN_56}
              fontWeight="bold"
              fontSize={14}
            >{`Parent supervision: ${formatSecondsToHHMM2(data.value)}`}</Typography>
          );
        }
        if (data.dataKey === 'session') {
          return (
            <Typography
              key={data.dataKey}
              color={FORTA_DOCS.SN_53}
              fontWeight="bold"
              fontSize={14}
            >{`Session completed: ${formatSecondsToHHMM(data.value)}`}</Typography>
          );
        }
      })}
    </Box>
  );
};

const formatTickToHHMM = (tick: number) => formatSecondsToHHMM(tick);

function UtilizationLoader() {
  return (
    <Box data-testid="UtilizationLoader-loader">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          ml: 8.5,
          px: 5,
          mt: 1,
          borderLeft: `1px solid ${FORTA_GRAY2}`,
        }}
      >
        {Array.from({ length: 6 }).map((_, key) => (
          <Box
            key={key}
            sx={{
              height: 260,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'flex-end',
            }}
          >
            <Skeleton variant="rectangular" width={20} height={200} />
            <Skeleton sx={{ ml: 3 }} variant="rectangular" width={20} height={140} />
          </Box>
        ))}
      </Box>
      <Skeleton sx={{ ml: 7 }} height={45} />
    </Box>
  );
}

function SessiontUtilizationLoader() {
  return (
    <Box
      data-testid="SessiontUtilizationLoader-loader"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        px: 5,
        ml: 8.5,
        borderLeft: `1px solid ${FORTA_GRAY2}`,
      }}
    >
      {Array.from({ length: 6 }).map((_, key) => (
        <Box
          key={key}
          sx={{
            height: 300,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Skeleton variant="rectangular" width={65} height={200} />
        </Box>
      ))}
    </Box>
  );
}

interface SixWeekDocumentUtilizationSummaryProps {
  selectedClient: IOption | null;
  clientParentUtilLoading: boolean;
  sessionUtilLoading: boolean;
  data: WeeklyUtilization[];
  sx?: any;
}

const getTickInterval = (maxValue: number) => {
  if (maxValue <= 3600) return 900; // Up to 1 hour: 15-minute intervals
  if (maxValue <= 18000) return 3600; // Up to 5 hours: 1-hour intervals
  return 18000; // Above 5 hours: 5-hour intervals
};

function SixWeekDocumentUtilizationSummary({
  selectedClient,
  data = [],
  clientParentUtilLoading,
  sessionUtilLoading,
  sx = {},
}: SixWeekDocumentUtilizationSummaryProps) {
  const { t } = useTranslation('dashboard');
  const shiftAmount = 12;
  const maxSessionValue = Math.max(...data.map((d) => d.session));
  const sessionTickInterval = getTickInterval(maxSessionValue);
  const maxSNValue = Math.max(
    ...data.flatMap((d) => [d.clientSN, d.parentSN, d.clientSNExpectedHours]),
  );
  const snTickInterval = getTickInterval(maxSNValue);
  return (
    <Box
      data-testid="SixWeekDocumentUtilizationSummaryContainer"
      sx={{
        borderRadius: '10px',
        backgroundColor: FORTA_WHITE,
        height: '100%',
        minHeight: selectedClient ? '675px' : 0,
        pt: 2,
        pl: 0,
        pb: 0,
        ...sx,
      }}
    >
      {selectedClient ? (
        <Box>
          {clientParentUtilLoading ? (
            <UtilizationLoader />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} margin={{ top: 10, left: 10 }}>
                <XAxis
                  dataKey="name"
                  dy={8}
                  tickLine={false}
                  tick={{ fontWeight: 'bold', fontSize: '14px', transform: 'translate(5, 0)' }}
                />
                <YAxis
                  tickFormatter={formatTickToHHMM}
                  domain={[0, Math.ceil(maxSNValue / snTickInterval) * snTickInterval]}
                  ticks={Array.from(
                    { length: Math.ceil(maxSNValue / snTickInterval) + 1 },
                    (_, i) => i * snTickInterval,
                  )}
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  content={(data: any) => {
                    return CustomTooltip(t, data);
                  }}
                />

                <Bar dataKey="clientSNExpectedHours" fill={FORTA_GRAY2} barSize={20}>
                  {data.map((_, index) => (
                    <Cell key={`cell-${index}`} transform={`translate(${shiftAmount}, 0)`} />
                  ))}
                </Bar>
                <Bar dataKey="clientSN" fill={FORTA_DOCS.CSN_55} barSize={20}>
                  {data.map((_, index) => (
                    <Cell key={`cell-${index}`} transform={`translate(${-shiftAmount}, 0)`} />
                  ))}
                </Bar>

                <Bar dataKey="parentSN" fill={FORTA_DOCS.PSN_56} barSize={20} />

                <CartesianGrid stroke={FORTA_WHITE} vertical={false} />
              </BarChart>
            </ResponsiveContainer>
          )}

          {sessionUtilLoading ? (
            <SessiontUtilizationLoader />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} margin={{ bottom: 10, top: 12, left: 10 }}>
                <XAxis dataKey="name" hide />
                <YAxis
                  type="number"
                  reversed
                  tickFormatter={formatTickToHHMM}
                  domain={[
                    0,
                    Math.ceil(maxSessionValue / sessionTickInterval) * sessionTickInterval,
                  ]}
                  interval={0}
                  ticks={Array.from(
                    { length: Math.ceil(maxSessionValue / sessionTickInterval) + 1 },
                    (_, i) => i * sessionTickInterval,
                  )} // Generate ticks based on the interval
                />
                <Tooltip
                  cursor={{ fill: 'transparent' }}
                  content={(data: any) => {
                    return CustomTooltip(t, data);
                  }}
                />

                <Bar dataKey="session" fill={FORTA_SUCCESS} barSize={60}>
                  {data.map((_, index) => (
                    <Cell key={`cell-${index}`} transform={`translate(${6}, 0)`} />
                  ))}
                </Bar>
                <CartesianGrid stroke={FORTA_WHITE} vertical={false} />
              </BarChart>
            </ResponsiveContainer>
          )}
          {!clientParentUtilLoading && !sessionUtilLoading && (
            <>
              <Box sx={{ backgroundColor: FORTA_GRAY2, height: 42, mt: -41.2, ml: 8.8 }}></Box>
              <Box sx={{ display: 'flex', ml: 3, mt: 38, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
                  <Box
                    sx={{
                      height: 20,
                      width: 20,
                      backgroundColor: FORTA_DOCS.SN_53,
                      mr: 1,
                      borderRadius: '4px',
                    }}
                  />
                  <Typography>Sessions (97153)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
                  <Box
                    sx={{
                      height: 20,
                      width: 20,
                      backgroundColor: FORTA_DOCS.CSN_55,
                      mr: 1,
                      borderRadius: '4px',
                    }}
                  />
                  <Typography>Client supervision (97155)</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box
                    sx={{
                      height: 20,
                      width: 20,
                      backgroundColor: FORTA_DOCS.PSN_56,
                      mr: 1,
                      borderRadius: '4px',
                    }}
                  />
                  <Typography>Parent supervision (97156)</Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <ErrorAlertIcon width={18} height={18} viewBox="0 0 26 24" />
          <Typography fontSize={16} textAlign="center" my={2} color={FORTA_BLUE.primary}>
            Please select a client to view utilization
          </Typography>
        </Box>
      )}
    </Box>
  );
}

export default SixWeekDocumentUtilizationSummary;
